import { useCallback, useState } from "react";
import { useAuth } from "@/context/auth/AuthContext";
import { uploadImage } from "@/api/image";
import { optionsPosition } from "@/lib/image";
import { Attachment as UploadAttachmentPayload } from "@/lib/types";
import { extractUploadErrorMessage } from "@/components/images/batchUploadErrors";
import { queryClient } from "@/lib/queryClient";
import { ImportTable } from "@/lib/import/import-types";
import { ImageLinkItem } from "@/api/image-link";

/**
 * Boucle de rattachement d'images.
 *
 * Frere de `useBatchImport`, pas une extension : celui-la **cree** une fiche par
 * image puis annule en cas d'echec ; celui-ci **rattache a l'existant**. Fusionner
 * les deux aurait impose un drapeau `mode` traversant chaque branche.
 *
 * Difference importante : **aucun rollback n'est necessaire**. Aucune fiche n'est
 * creee, donc un upload en echec laisse la fiche existante intacte — strictement
 * plus sur que le batch-import, ou l'echec laissait une fiche vide a supprimer.
 */

export type ImageLinkItemStatus =
  | "pending"
  | "uploading"
  | "success"
  | "error"
  | "skipped";

export interface ImageLinkRunItem {
  uid: string;
  file: File;
  fileName: string;
  recordId: string;
  recordLabel: string;
  position: string;
  order: number;
  status: ImageLinkItemStatus;
  error?: string;
  /** miniature renvoyee par le converter, pour le recapitulatif */
  thumbnailUrl?: string;
}

/** Options d'écriture appliquées à tout le lot, choisies par l'utilisateur. */
export interface ImageLinkRunOptions {
  /** appliquer le filigrane sur les images (défaut : oui) */
  applyWatermark?: boolean;
  /** conserver la version HD (défaut : non) */
  isHd?: boolean;
}

/** Erreur de quota : on arrete tout le lot, comme le batch-import existant. */
const STOP_BATCH_ERROR = "quotaExceded";

/**
 * Uploads simultanes.
 *
 * 400 allers-retours sequentiels au converter (~1,5 s chacun) feraient dix
 * minutes ; a trois en parallele on tombe vers trois minutes et demie. Ne pas
 * augmenter sans mesurer la memoire de Sharp cote converter.
 */
const CONCURRENCY = 3;

export function useImageLinkImport(table: ImportTable) {
  const { instanceData } = useAuth();
  const [items, setItems] = useState<ImageLinkRunItem[]>([]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const patch = useCallback(
    (uid: string, changes: Partial<ImageLinkRunItem>) => {
      setItems((current) =>
        current.map((item) => (item.uid === uid ? { ...item, ...changes } : item)),
      );
    },
    [],
  );

  const reset = useCallback(() => {
    setItems([]);
    setRunning(false);
    setDone(false);
  }, []);

  /** Construit la file a partir du plan approuve et des fichiers choisis. */
  const prepare = useCallback(
    (files: File[], accepted: ImageLinkItem[]) => {
      const byName = new Map(files.map((file) => [file.name, file]));
      const queue: ImageLinkRunItem[] = [];

      for (const entry of accepted) {
        const file = byName.get(entry.fileName);
        if (!file || !entry.recordId || !entry.position) continue;
        queue.push({
          uid: `${entry.fileName}-${file.size}`,
          file,
          fileName: entry.fileName,
          recordId: entry.recordId,
          recordLabel: entry.recordLabel ?? "",
          position: entry.position,
          order: entry.order ?? 1,
          status: "pending",
        });
      }

      setItems(queue);
      setDone(false);
      return queue;
    },
    [],
  );

  const run = useCallback(
    async (
      queue: ImageLinkRunItem[],
      options: ImageLinkRunOptions = {},
    ) => {
      if (!instanceData || queue.length === 0) return;
      // Filigrane et version HD : réglés par l'utilisateur pour tout le lot. Le
      // filigrane reste appliqué par défaut (comportement historique), la HD
      // reste désactivée par défaut (poids/stockage).
      const applyWatermark = options.applyWatermark ?? true;
      const isHd = options.isHd ?? false;
      setRunning(true);

      // L'image « face » (image principale du type, 1re position de la liste)
      // est publiée d'office : c'est celle qu'on veut voir sur le front. Les
      // autres vues (dos, détail…) restent en brouillon, à publier à la main.
      const mainPosition =
        optionsPosition[table as keyof typeof optionsPosition]?.[0];

      let stopped = false;
      let cursor = 0;

      const worker = async () => {
        for (;;) {
          if (stopped) return;
          const index = cursor;
          cursor += 1;
          const item = queue[index];
          if (!item) return;

          patch(item.uid, { status: "uploading" });
          try {
            // Position « face » (image principale) : une seule image par fiche,
            // via l'endpoint « unique » qui écrase le même couple (fiche,
            // position). Toutes les autres positions acceptent plusieurs images
            // différentes : on passe alors par « create » (sans unicité), sinon
            // la 2e image écraserait la 1re. C'est le comportement attendu par
            // le client : « à part la face, plusieurs images à la même position ».
            const isMainPosition = item.position === mainPosition;
            const attachment = await uploadImage(
              item.file,
              {
                name: item.fileName,
                position: item.position,
                docId: item.recordId,
                order: item.order,
                published: isMainPosition,
                applyWatermark,
                isHd,
              } as unknown as UploadAttachmentPayload,
              instanceData.s3Bucket,
              instanceData.id,
              isMainPosition ? undefined : "back/api/attachment/create",
              item.fileName,
            );

            patch(item.uid, {
              status: "success",
              thumbnailUrl:
                attachment?.mediumPublicUrl ??
                attachment?.smallPublicUrl ??
                attachment?.publicUrl,
            });
          } catch (error) {
            const message = extractUploadErrorMessage(error);
            // Quota atteint : inutile de continuer, tout le reste echouerait.
            if (message === STOP_BATCH_ERROR) {
              stopped = true;
              patch(item.uid, { status: "error", error: message });
              return;
            }
            // Aucune fiche n'a ete creee : rien a annuler, la fiche existante est
            // intacte.
            patch(item.uid, { status: "error", error: message });
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker),
      );

      if (stopped) {
        setItems((current) =>
          current.map((item) =>
            item.status === "pending" ? { ...item, status: "skipped" } : item,
          ),
        );
      }

      // Rafraichir les listes de fiches ET les vignettes. Les miniatures du
      // dashboard viennent de la requete ["artworks", "attachments", …] : sans
      // l'invalider avec ce prefixe exact, il fallait ouvrir puis quitter une
      // fiche pour voir la nouvelle image apparaitre.
      for (const key of [
        [table],
        ["artworks", "attachments"],
        ["countAllItems"],
      ]) {
        queryClient.invalidateQueries({ queryKey: key });
      }

      setRunning(false);
      setDone(true);
    },
    [instanceData, patch, table],
  );

  const finished = items.filter((item) =>
    ["success", "error", "skipped"].includes(item.status),
  ).length;

  return {
    items,
    running,
    done,
    total: items.length,
    finished,
    percent: items.length > 0 ? Math.round((finished / items.length) * 100) : 0,
    successCount: items.filter((item) => item.status === "success").length,
    errorCount: items.filter((item) => item.status === "error").length,
    inFlight: items.find((item) => item.status === "uploading"),
    prepare,
    run,
    reset,
  };
}

export type UseImageLinkImportReturn = ReturnType<typeof useImageLinkImport>;
